import { createContext, useContext } from 'react';

import type { ReactNode } from 'react';
import type { FieldValues } from '@toolbox/form';
import type { UseStepFormMethods } from './useStepForm';

export type StepContextValue<FD extends FieldValues = FieldValues> = UseStepFormMethods<FD> & {
  fetching: boolean;
};

const StepContext = createContext<StepContextValue | null>(null);

/**
 * hook to access the step context
 *
 * @returns StepContext
 */
export const useStepContext = (): StepContextValue => {
  const context = useContext(StepContext);

  if (!context) throw new Error('useStepContext is used outside of StepContext.Provider');

  return context;
};

export type StepContextProviderProps<FD extends FieldValues = FieldValues> =
  StepContextValue<FD> & { children: ReactNode };

export const StepContextProvider = <FD extends FieldValues = FieldValues>({
  children,
  ...value
}: StepContextProviderProps<FD>): JSX.Element => (
  <StepContext.Provider value={value}>{children}</StepContext.Provider>
);
